(ns minorodata.components.webforms.core
  (:require ["react" :as react]
            [applied-science.js-interop :as jsi]
            [clojure.string :as str]
            ["lucide-react" :refer [ChevronLeft ChevronRight CircleHelp Plus
                                    Search Trash2]]
            [minorodata.app.handlers :refer [dispatch dispatch-sync]]
            [minorodata.app.utils :as app.utils]
            [minorodata.components.spinner.core :refer [spinner-lucide]]
            [minorodata.components.webforms.drawers :as webforms.drawers]
            [minorodata.components.webforms.grid :as webforms.grid]
            [minorodata.webforms.model :refer [->webform-table-screen]]
            [mold-forms.core :as form-component]
            [mold-forms.model :refer [->form-model]]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [reitit.frontend.easy :as rt.easy]
            [ui.button :refer [button]]
            [ui.card :refer [card]]
            [ui.date-time :refer [get-browser-timezone]]
            [ui.fields :refer [input select select-trigger-class]]
            [ui.page :refer [page-columns page-content page-root]]
            [ui.skeleton :refer [skeleton]]
            [ui.tooltip :as tooltip]
            [ui.typography :as typography]
            [ui.utils :refer [clsx]]))

(defn loading-card [{:keys [classes]}]
  ^{:key (random-uuid)}
  [:div {:class (:webform-card classes)}
   [card {:class "p-4"}
    [:div {:class "flex flex-col gap-2"}
     [skeleton {:class "mb-1 " :style {:height 27}}]
     [skeleton {:style {:width "40%" :height 16}}]
     [skeleton {:style {:width "70%" :height 16}}]]

    [:footer
     [:div {:class "flex mt-4"}
      [skeleton {:style {:width 70 :height 29}}]]]]])


(defn trim-webform-prefix-suffix [webform-name]
  (-> webform-name
      (clojure.string/replace #"(^KLEENE_WEBFORMS\.|_WF$)" "")))


(defn close-panel []
  (dispatch [:app.events/navigate :webforms]))

(defn webform-row [{:keys         [active-table-name]
                    :webform/keys [tablename username created-at last-modified id]}]
  (let [active?  (= active-table-name tablename)
        timezone (or @(re-posh/subscribe [:app.subs/timezone]) (get-browser-timezone))]
    [:a {:href          (rt.easy/href :webforms-table {:table-name tablename})
         :class         (clsx "block min-w-0 px-5 py-3 hover:bg-kleene-hover-background transition-colors border-b cursor-pointer"
                              {"bg-kleene-selected-background" active?})
         :data-cy       (str "webform-" id)
         :data-selected (when active? "true")}
     [typography/h3 {:data-cy "webform-card-title"
                     :class   "truncate"}
      (trim-webform-prefix-suffix tablename)]
     [:div {:class "mt-1"}
      [typography/small {:class "text-kleene-text-secondary"}
       "Updated " (app.utils/format-date (or last-modified created-at) {:timezone-to timezone})
       (when username (str " by " username))]]]))

(defn filter-webforms [webforms search]
  (if (str/blank? search)
    webforms
    (let [search (str/lower-case (str/trim search))]
      (filter #(str/includes? (str/lower-case (trim-webform-prefix-suffix (:webform/tablename %)))
                              search)
              webforms))))

(defn list-message [text]
  [:div {:class "px-5 py-5"}
   [:p text]])

(defn sidebar [{:keys [webforms loading? active-table-name on-create]}]
  (let [[search set-search] (react/useState "")
        visible            (filter-webforms webforms search)]
    [:div {:class "flex flex-col overflow-hidden"}
     [:header {:class "shrink-0 border-b px-5 py-4 flex flex-col bg-kleene-background gap-2"}
      [:div {:class "flex justify-between items-center"}
       [typography/h2 "Webforms"]
       [button {:variant  :secondary
                :size     :icon-md
                :icon     Plus
                :title    "Create webform"
                :data-cy  "main-create-webform-button"
                :on-click on-create}]]
      [input {:class         "min-w-0"
              :placeholder   "Search by name"
              :default-value search
              :type          "search"
              :on-change     #(set-search (jsi/get-in % [:target :value]))
              :name          "search-entity"
              :input-props   {:auto-complete "off"}
              :icon          Search}]]
     [:div {:class "flex-1 overflow-auto"}
      (cond
        (seq visible)
        [:div {:data-cy "webforms-cards"}
         (for [webform visible]
           ^{:key (:webform/id webform)}
           [webform-row (assoc webform :active-table-name active-table-name)])]

        loading?
        nil

        (seq webforms)
        [list-message "No webforms match your search."]

        :else
        [list-message "No webforms yet."])
      (when loading?
        [:div {:class "flex justify-center py-4 px-5"}
         [spinner-lucide]])]]))

(defn loading-table []
  [card
   [:div {:class "p-4 border-b"}
    [skeleton {:height 30 :width "45%"}]]

   [:div {:class "p-4 border-b"}
    [skeleton {:width "100%"}]]

   [:div {:class "p-4 border-b mb-20"}
    [skeleton {:width "100%"}]]

   [:div {:class "p-4 flex justify-end"}
    [skeleton {:width "20%" :style {:display "inline-block"}}]]])

(defn convert-column [header]
  (let [{:keys [column-name field-name is-nullable data-type]} header
        is-nullable (= "YES" is-nullable)]
    (when-not (clojure.string/starts-with? field-name "-kwf-")
      {:title       (str column-name)
       :type        (if (= data-type "NUMBER") "numeric" "text")
       :field       (keyword field-name)
       :is-nullable is-nullable})))


(defn set-rows [rows-per-page]
  (dispatch-sync [:webform-table-screen/update
                  (->webform-table-screen
                   {:items-per-page rows-per-page})])
  (dispatch [:webforms.api/load-form-details]))

(defn set-page [page]
  (dispatch-sync [:webform-table-screen/update
                  (->webform-table-screen
                   {:page page})])
  (dispatch [:webforms.api/load-form-details]))

(defn data-table [{:keys [webform-table updating? set-updating submit-ref]}]
  (let [{:webform-table-screen/keys [table-name items-per-page items-total total-pages data columns page]} webform-table
        columns-data             (react/useMemo #(vec (keep convert-column columns))
                                                 #js [columns])
        fields                   (react/useMemo #(mapv :field columns-data)
                                                 #js [columns-data])
        dark-mode?               @(re-posh/subscribe [:app/prop :app/dark-mode?])
        data-loading?            @(re-posh/subscribe [:mold-http/loading? :webforms.api/load-form-details])
        api-ref                  (react/useRef nil)
        deleted-ids-ref          (react/useRef #{})
        touched-cells-ref        (react/useRef #{})
        new-row-uuids-ref        (react/useRef {})
        submitting-ref           (react/useRef false)
        [selection set-selection] (react/useState {:count 0 :deleted 0})
        loading?                  (or data-loading? updating?)
        baseline                  (react/useMemo #(webforms.grid/baseline-by-id data)
                                                 #js [data])
        rows                      (react/useMemo
                                   #(webforms.grid/prepare-rows data fields items-per-page)
                                   #js [data fields items-per-page])
        on-state-change
        (fn []
          (when-let [api (.-current api-ref)]
            (let [selected (map #(js->clj % :keywordize-keys true)
                                (array-seq (jsi/call api :getSelectedRows)))
                  deleted  (.-current deleted-ids-ref)]
              (set-selection
               {:count (count selected)
                :deleted (count (filter #(contains? deleted
                                                     (get % webforms.grid/row-id-key))
                                        selected))}))))
        on-submit
        (fn []
          (when-not (.-current submitting-ref)
            (when-let [api (.-current api-ref)]
              (jsi/call api :stopEditing)
              (let [new-row-uuid
                    (fn [row]
                      (let [client-id (get row webforms.grid/row-id-key)]
                        (or (get (.-current new-row-uuids-ref) client-id)
                            (let [uuid (str (random-uuid))]
                              (set! (.-current new-row-uuids-ref)
                                    (assoc (.-current new-row-uuids-ref) client-id uuid))
                              uuid))))
                    diffs (webforms.grid/build-diffs
                           (webforms.grid/current-rows api) baseline fields
                           (.-current deleted-ids-ref) new-row-uuid)]
                (when (seq diffs)
                  (set! (.-current submitting-ref) true)
                  (set-updating true)
                  (dispatch [:webforms.edit/bulk
                             {:form-id table-name
                              :data diffs
                              :on-success
                              (fn []
                                (set! (.-current deleted-ids-ref) #{})
                                (set! (.-current touched-cells-ref) #{})
                                (set! (.-current new-row-uuids-ref) {})
                                (set! (.-current submitting-ref) false)
                                (set-updating false))
                              :on-failure
                              (fn []
                                (set! (.-current submitting-ref) false)
                                (set-updating false))}]))))))]
    (set! (.-current submit-ref) on-submit)
    (react/useEffect
     (fn []
       #(set! (.-current submit-ref) nil))
     #js [])
    (react/useEffect
     (fn []
       (set! (.-current deleted-ids-ref) #{})
       (set! (.-current touched-cells-ref) #{})
       (set! (.-current new-row-uuids-ref) {})
       (set! (.-current submitting-ref) false)
       (set-selection {:count 0 :deleted 0})
       js/undefined)
     #js [data fields items-per-page page])
    [:<>
     [:div {:class "relative"}
      (when loading?
        [:div {:class "absolute inset-0 bg-kleene-background/50 flex justify-center items-center z-997"}
         [spinner-lucide {:size 24}]])

      (when (pos? (:count selection))
        [:div {:class "flex items-center justify-end gap-2 border-b px-4 py-2"}
         [:span {:class "text-sm text-kleene-text-secondary"}
          (str (:count selection)
               (if (= 1 (:count selection)) " row selected" " rows selected"))]
         (when (pos? (:deleted selection))
           [button {:size :small :variant :empty
                    :disabled loading?
                    :on-click #(webforms.grid/restore-selected!
                                (.-current api-ref) deleted-ids-ref on-state-change)}
            "Restore selected rows"])
         (when (< (:deleted selection) (:count selection))
           [button {:size :small :variant :error-empty
                    :disabled loading?
                    :on-click #(webforms.grid/delete-selected!
                                (.-current api-ref) deleted-ids-ref on-state-change)}
            "Delete selected rows"])])

      [:f> webforms.grid/table
       {:api-ref api-ref
        :rows rows
        :columns columns-data
        :fields fields
        :baseline baseline
        :page page
        :items-per-page items-per-page
        :dark-mode? dark-mode?
        :deleted-ids-ref deleted-ids-ref
        :touched-cells-ref touched-cells-ref
        :on-state-change on-state-change}]

      [:div {:class "w-full"}
       [button {:icon       Plus
                :variant    :empty
                :full-width true
                :class      "rounded-none *:w-auto"
                :disabled   loading?
                :on-click   (fn []
                              (when-let [api (.-current api-ref)]
                                (webforms.grid/add-row! api fields)
                                (on-state-change)))}
        "Add new row"]]]
     [:div {:class "flex justify-end px-6 py-3"}
      [:div {:class "flex items-center gap-2 shrink-0"}
       [select {:value     items-per-page
                :on-change set-rows
                :native    false
                :disabled  loading?
                :options   [{:value 50 :text [:span [:strong {:class "font-bold"} "50"] " per page"]}
                            {:value 100 :text [:span [:strong {:class "font-bold"} "100"] " per page"]}
                            {:value 250 :text [:span [:strong {:class "font-bold"} "250"] " per page"]}
                            {:value 500 :text [:span [:strong {:class "font-bold"} "500"] " per page"]}]}]
       [:div {:class (clsx (select-trigger-class {})
                           "cursor-default gap-2 pl-3 pr-0 py-0 justify-between w-auto shrink-0")}
        [:span
         [:strong {:class "font-bold"}
          (inc (* (dec page) items-per-page)) " - " (min (* page items-per-page) items-total)]
         " of " items-total]
        [:div {:class "-my-3 border-l h-full"}
         [button {:size     :icon
                  :variant  :empty-transparent
                  :icon     ChevronLeft
                  :disabled (or loading? (<= page 0))
                  :on-click #(set-page (dec page))}]
         [button {:size     :icon
                  :variant  :empty-transparent
                  :icon     ChevronRight
                  :disabled (or loading? (>= page total-pages))
                  :on-click #(set-page (inc page))}]]]]]]))

(defn use-init-webform-table [table-name]
  (react/useEffect
   (fn []
     (dispatch-sync [:webform-table-screen/add
                     (->webform-table-screen
                      {:table-name     table-name
                       :data           []
                       :columns        []
                       :order-by       nil
                       :order-dir      nil
                       :total-pages    0
                       :page           1
                       :items-per-page 50})
                     {:replace true}])
     (dispatch-sync [:webform-table-screen/update
                     (->webform-table-screen
                      {:filter-field nil
                       :filter-type  nil
                       :filter-value nil})
                     {:replace true}])
     (dispatch-sync [:webforms.api/load-form-details])
     (fn []))
   #js []))

(defn webforms-table [{:keys [updating? set-updating submit-ref]}]
  (let [route             @(re-posh/subscribe [:app-route/pull])
        data-loading?     @(re-posh/subscribe [:mold-http/loading? :webforms.api/load-form-details])
        update-loading?   @(re-posh/subscribe [:mold-http/loading? :webforms.edit/save-row])
        delete-loading?   @(re-posh/subscribe [:mold-http/loading? :webforms.edit/delete-row])
        save-new-loading? @(re-posh/subscribe [:mold-http/loading? :webforms.edit/save-new-row])
        webform-table     @(re-posh/subscribe [:webform-table-screen/pull])

        loading?          (or data-loading? update-loading? delete-loading? save-new-loading?)
        {:webform-table-screen/keys [data]} webform-table]

    (use-init-webform-table (get-in route [:app-route/params :table-name]))

    (if (and loading? (empty? data))
      [loading-table]
      [:f> data-table {:webform-table webform-table
                       :updating?     updating?
                       :set-updating  set-updating
                       :submit-ref    submit-ref}])))


(defn set-filter [form-data]
  (let [data (form-component/model->fields form-data)
        {:keys [filter-field filter-type filter-value]} data]
    (dispatch-sync [:webform-table-screen/update
                    (->webform-table-screen
                     {:filter-field filter-field
                      :filter-type  filter-type
                      :filter-value filter-value})
                    {:replace true}])
    (dispatch [:webforms.api/load-form-details])))


(defn clear-filter []
  (dispatch-sync [:form-model/update [:form-model/id :webforms-filter-form]
                  (->form-model {:id        :webforms-filter-form
                                 :on-submit set-filter
                                 :fields    [{:name    :filter-field
                                              :value   ""
                                              :process identity}
                                             {:name    :filter-type
                                              :value   ""
                                              :process identity}
                                             {:name    :filter-value
                                              :value   ""}]})])
  (set-filter {:filter-field ""
               :filter-type  ""
               :filter-value ""}))

(defn webform-filters-render [{:keys [fields errors on-submit columns]}]
  [:div {:class "flex items-end"}
   [:form {:class     "flex gap-2 items-end"
           :on-submit on-submit}

    [select
     {:field   (:filter-field fields)
      :errors  errors
      :native  false
      :class   "min-w-[180px]"
      :id      :filter-field
      :label   "Filter field"
      :placeholder "None"
      :options (mapv (fn [option] {:value option :text option})
                     columns)}]

    [select
     {:field   (:filter-type fields)
      :errors  errors
      :native  false
      :class   "min-w-[180px]"
      :id      :filter-type
      :label   "Type"
      :placeholder "None"
      :options (mapv (fn [option] {:value option :text option})
                     ["like" "=" "<" "<=" ">" ">=" "!="])}]

    [input
     {:field  (:filter-value fields)
      :errors errors
      :id     :filter-value
      :label  "Value"}]

    [button {:type "submit"}
     "Apply"]

    [button {:variant  :empty
             :on-click clear-filter
             :class    "shrink-0"}
     "Clear filters"]]])


(defn drop-filter-form []
  (dispatch [:form-model/delete [:form-model/id :webforms-filter-form]])
  (dispatch [:webform-table-screen/update
             (->webform-table-screen
              {:filter-field nil
               :filter-type  nil
               :filter-value nil})
             {:replace true}]))


(defn webforms-header []
  (let [table-name   (re-posh/subscribe [:webform-table-screen/prop :webform-table-screen/table-name])
        filter-field (re-posh/subscribe [:webform-table-screen/prop :webform-table-screen/filter-field])
        filter-type  (re-posh/subscribe [:webform-table-screen/prop :webform-table-screen/filter-type])
        filter-value (re-posh/subscribe [:webform-table-screen/prop :webform-table-screen/filter-value])
        columns      (re-posh/subscribe [:webform-table-screen/prop :webform-table-screen/columns])]
    (reagent/create-class
     {:display-name
      "webforms-header"

      :component-will-unmount
      drop-filter-form

      :render
      (fn []
        ^{:key (str @table-name @filter-field @filter-type @filter-value)}
        [:div
         [form-component/form
          (->form-model
           {:id        :webforms-filter-form
            :on-submit set-filter
            :fields    [{:name    :filter-field
                         :value   @filter-field
                         :process identity}
                        {:name    :filter-type
                         :value   @filter-type
                         :process identity}
                        {:name    :filter-value
                         :value   (or @filter-value "")}]})
          (fn [fields errors on-submit]
            [webform-filters-render
             {:on-submit on-submit
              :errors    errors
              :fields    fields
              :columns   (->> @columns
                              (filter #(not (str/starts-with? (:field-name %) "-kwf-")))
                              (map :column-name))}])]])})))


(defn selected-webform [{:keys [table-name webform on-delete]}]
  (let [data-loading?            @(re-posh/subscribe [:mold-http/loading? :webforms.api/load-form-details])
        [updating? set-updating] (react/useState false)
        submit-ref               (react/useRef nil)
        selected-ref             (react/useRef nil)]
    (react/useEffect
     (fn []
       (set! (.-current selected-ref) table-name)
       #(set! (.-current selected-ref) nil))
     #js [table-name])
    [:div {:class "flex flex-col h-full overflow-hidden"}
     [:header {:class "shrink-0 border-b px-6 py-4 flex flex-col gap-4"}
      [:div {:class "flex items-start justify-between gap-4"}
       [:div {:class "min-w-0"}
        [typography/h2 {:class "break-all"}
         (trim-webform-prefix-suffix table-name)]
        [typography/p {:class "mt-1 break-all text-kleene-text-secondary"}
         table-name]]
       [:div {:class "shrink-0 flex items-center gap-2"}
        [tooltip/tooltip {:content [:div {:class "flex flex-col gap-1"}
                                    [:p "Add or edit values directly in the table."]
                                    [:p "Select one or more rows and use Delete selected rows. Deleted saved rows can be restored before Save."]
                                    [:p "Unsaved edits are discarded when you change page, page size, or filters."]]
                          :content-props {:side "bottom"}}
         [:> CircleHelp {:class "cursor-help text-kleene-text-secondary"}]]
        [button {:on-click #(when-let [submit (.-current submit-ref)] (submit))
                 :disabled (or data-loading? updating?)}
         "Save changes"]
        (when webform
          [tooltip/tooltip {:content "Delete webform"}
           [:div
            [button {:size       :icon-md
                     :variant    :secondary
                     :icon       Trash2
                     :class      "text-kleene-error-01"
                     :aria-label "Delete webform"
                     :data-cy    "webform-delete-btn"
                     :on-click   #(on-delete (fn []
                                               (when (= table-name (.-current selected-ref))
                                                 (close-panel))))}]]])]]
      [webforms-header]]
     [:div {:class "flex-1 overflow-auto pb-kai"}
      ^{:key table-name}
      [:f> webforms-table {:updating?    updating?
                           :set-updating set-updating
                           :submit-ref   submit-ref}]]]))


(defn empty-panel []
  [:div {:class "flex h-full items-center justify-center"}
   [typography/h2 "Select a webform"]])


(defn webforms-page []
  (let [drawer (reagent/atom nil)]

    (dispatch [:webforms.api/fetch-webforms])

    (fn []
      (let [{:app-route/keys [params query]} @(re-posh/subscribe [:app-route/pull])
            table-name (:table-name params)
            loading?   @(re-posh/subscribe [:mold-http/loading? :webforms.api/fetch-webforms])
            webforms   @(re-posh/subscribe [:webform/pull-many])
            webform    (when table-name
                         (some #(when (= table-name (:webform/tablename %)) %) webforms))]
        [:<>
         [page-root
          [page-content {:variant :border}
           [page-columns
            [:f> sidebar {:webforms          webforms
                          :loading?          loading?
                          :active-table-name table-name
                          :on-create         #(dispatch [:app.events/navigate :webforms nil {:create true}])}]
            (cond
              table-name
              [:f> selected-webform {:table-name table-name
                                     :webform    webform
                                     :on-delete  #(reset! drawer {:type :delete-webform :on-success %})}]

              (:create query)
              [webforms.drawers/create-webform-panel {:on-close close-panel}]

              :else
              [empty-panel])]]]

         [webforms.drawers/delete-webform
          {:open       (= :delete-webform (:type @drawer))
           :on-close   #(reset! drawer nil)
           :on-success (:on-success @drawer)
           :webform    webform}]]))))
